import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, ChevronLeft, ChevronRight, Phone, Car, RotateCw, MapPin, Check, X, User, History, ShoppingBag, Calendar, MessageCircle, Mail } from 'lucide-react';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../mock';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';

const tipos = ['Ligação', 'Test-drive', 'Retorno', 'Visita'];
const tipoIcon = {
  'Ligação': <Phone className="w-3 h-3" />,
  'Test-drive': <Car className="w-3 h-3" />,
  'Retorno': <RotateCw className="w-3 h-3" />,
  'Visita': <MapPin className="w-3 h-3" />,
};
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WD = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const empty = { titulo: '', cliente: '', clienteId: '', tipo: 'Retorno', vendedor: '', data: '', hora: '09:00', observacoes: '' };

const initials = (n) => (n || '?').split(' ').slice(0, 2).map((x) => x[0]).join('').toUpperCase();

const CustomerHistoryModal = ({ data, onClose }) => {
  if (!data) return null;
  const { customer, deals = [], atendimentos = [], appointments = [], bdc_leads = [], crm_leads = [] } = data;
  const interesses = [...new Set([...bdc_leads.map((l) => l.vehicle_interest), ...crm_leads.map((l) => l.interesse), ...atendimentos.map((a) => a.veiculoInteresse)].filter(Boolean))];
  const pastAppts = appointments.filter((a) => a.status === 'Concluído' || a.data < new Date().toISOString().slice(0, 10));
  return createPortal(
    <div className="fixed inset-0 z-[90] flex justify-end" data-testid="agenda-customer-history">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl h-full bg-card border-l border-border overflow-y-auto p-6 animate-fade-up">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold">{initials(customer.nome)}</span>
            <div>
              <h3 className="font-display font-black text-lg" data-testid="history-customer-name">{customer.nome}</h3>
              <p className="text-xs text-muted-foreground">Cliente desde {formatDate(customer.criadoEm)} · {customer.origem}</p>
            </div>
          </div>
          <button data-testid="history-close-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-3 mt-4 text-sm flex-wrap">
          {customer.telefone && <a href={`tel:${customer.telefone}`} className="flex items-center gap-1.5 text-foreground/65 hover:text-foreground"><Phone className="w-4 h-4" />{customer.telefone}</a>}
          {customer.whatsapp && <a href={`https://wa.me/${customer.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300"><MessageCircle className="w-4 h-4" /></a>}
          {customer.email && <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 text-foreground/65 hover:text-foreground"><Mail className="w-4 h-4" />{customer.email}</a>}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="rounded-lg border border-border/70 bg-popover p-3 text-center"><p className="text-xl font-black text-primary">{customer.totalAtendimentos || atendimentos.length}</p><p className="text-[10px] text-muted-foreground tracking-wider">VISITAS</p></div>
          <div className="rounded-lg border border-border/70 bg-popover p-3 text-center"><p className="text-xl font-black text-emerald-400">{deals.length}</p><p className="text-[10px] text-muted-foreground tracking-wider">COMPRAS</p></div>
          <div className="rounded-lg border border-border/70 bg-popover p-3 text-center"><p className="text-xl font-black text-amber-400">{appointments.length}</p><p className="text-[10px] text-muted-foreground tracking-wider">AGENDAMENTOS</p></div>
        </div>

        {interesses.length > 0 && (
          <div className="mt-5">
            <h4 className="flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground font-semibold mb-2"><Car className="w-3.5 h-3.5" /> VEÍCULOS DE INTERESSE</h4>
            <div className="flex flex-wrap gap-1.5">{interesses.map((v, i) => <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/25 font-medium">{v}</span>)}</div>
          </div>
        )}

        <div className="mt-5">
          <h4 className="flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground font-semibold mb-2"><ShoppingBag className="w-3.5 h-3.5" /> HISTÓRICO DE COMPRAS ({deals.length})</h4>
          <div className="space-y-2">
            {deals.map((d) => (
              <div key={d.id} className="rounded-lg border border-border/70 bg-popover p-3">
                <p className="font-semibold text-sm">{d.vehicle_desc}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{formatDate(d.sale_date)} · {d.salesperson_name} · {formatCurrency(d.sale_price)}</p>
              </div>
            ))}
            {deals.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhuma compra ainda.</p>}
          </div>
        </div>

        <div className="mt-5">
          <h4 className="flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground font-semibold mb-2"><History className="w-3.5 h-3.5" /> VISITAS / ATENDIMENTOS ({atendimentos.length})</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {atendimentos.map((a) => (
              <div key={a.id} className="rounded-lg border border-border/70 bg-popover p-3">
                <div className="flex items-center justify-between"><p className="text-sm font-medium">{a.veiculoInteresse || 'Atendimento'}</p><span className="text-[10px] text-muted-foreground">{a.resultado}</span></div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{a.dataHora} · {a.vendedor || '—'} · {a.origem}</p>
              </div>
            ))}
            {atendimentos.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum atendimento registrado.</p>}
          </div>
        </div>

        <div className="mt-5">
          <h4 className="flex items-center gap-2 text-xs tracking-[0.2em] text-muted-foreground font-semibold mb-2"><Calendar className="w-3.5 h-3.5" /> AGENDAMENTOS ANTERIORES ({pastAppts.length})</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {pastAppts.map((ap) => (
              <div key={ap.id} className="flex items-center justify-between border border-border/70 rounded-lg p-3">
                <div><p className="text-sm font-medium">{ap.titulo}</p><p className="text-[11px] text-muted-foreground">{ap.tipo} · {formatDate(ap.data)} {ap.hora} · {ap.vendedor || ap.criadoPor || '—'}</p></div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ap.status === 'Concluído' ? 'text-emerald-400 bg-emerald-400/10' : 'text-muted-foreground bg-muted/40'}`}>{ap.status}</span>
              </div>
            ))}
            {pastAppts.length === 0 && <p className="text-sm text-muted-foreground/70">Nenhum agendamento anterior.</p>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Agenda = () => {
  const today = new Date();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [appts, setAppts] = useState([]);
  const [team, setTeam] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selDay, setSelDay] = useState(todayIso);
  const [dOpen, setDOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);
  const [showSuggest, setShowSuggest] = useState(false);
  const [history, setHistory] = useState(null);
  const suggestRef = useRef(null);

  const load = async () => {
    try {
      const [a, u, c] = await Promise.all([api.get('/appointments'), api.get('/users'), api.get('/customers')]);
      setAppts(a.data);
      setTeam(u.data.filter((x) => ['Salesperson', 'Sales Manager'].includes(x.role)));
      setCustomers(c.data);
    } catch (e) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1).getDay();
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [cursor]);

  const byDay = (dstr) => appts.filter((a) => a.data === dstr);
  const selAppts = byDay(selDay);

  const openNew = (dstr) => { setForm({ ...empty, data: dstr || selDay }); setDOpen(true); };

  const suggestions = useMemo(() => {
    const q = (form.cliente || '').toLowerCase().trim();
    if (q.length < 2) return [];
    return customers.filter((c) => `${c.nome} ${c.telefone}`.toLowerCase().includes(q)).slice(0, 6);
  }, [form.cliente, customers]);

  const pickCustomer = (c) => {
    setForm({ ...form, cliente: c.nome, clienteId: c.id });
    setShowSuggest(false);
  };

  const save = async () => {
    if (!form.titulo || !form.data) { toast({ title: 'Preencha título e data' }); return; }
    setSaving(true);
    try {
      await api.post('/appointments', form);
      setDOpen(false); setForm(empty); await load();
      toast({ title: 'Agendamento criado', description: `${form.titulo} \u2022 ${formatDate(form.data)} \u2022 Você será alertado 1h antes` });
    } catch (e) { toast({ title: 'Erro ao salvar' }); } finally { setSaving(false); }
  };

  const toggleStatus = async (ap) => {
    const status = ap.status === 'Concluído' ? 'Agendado' : 'Concluído';
    setAppts((prev) => prev.map((x) => x.id === ap.id ? { ...x, status } : x));
    try { await api.put(`/appointments/${ap.id}`, { ...ap, status }); } catch (e) { load(); }
  };

  const openHistory = async (ap) => {
    let cid = ap.clienteId;
    if (!cid && ap.cliente) {
      const match = customers.find((c) => c.nome.toLowerCase() === ap.cliente.toLowerCase());
      cid = match?.id;
    }
    if (!cid) { toast({ title: 'Cliente não vinculado', description: 'Este agendamento não está vinculado a um cliente da base.' }); return; }
    try { const r = await api.get(`/customers/${cid}/journey`); setHistory(r.data); }
    catch (e) { toast({ title: 'Erro ao carregar histórico' }); }
  };

  const move = (dir) => {
    setCursor((c) => {
      let m = c.m + dir, y = c.y;
      if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
      return { y, m };
    });
  };

  const input = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

  const longDate = (() => {
    const d = new Date(`${selDay}T12:00:00`);
    const s = d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const resumo = {
    total: selAppts.length,
    confirmados: selAppts.filter((a) => a.status === 'Confirmado').length,
    pendentes: selAppts.filter((a) => !a.status || ['Agendado', 'Pendente'].includes(a.status)).length,
    concluidos: selAppts.filter((a) => a.status === 'Concluído').length,
    cancelados: selAppts.filter((a) => a.status === 'Cancelado').length,
  };

  const statusCls = (s) => (s === 'Concluído' ? 't-ok' : s === 'Cancelado' ? 't-bad' : s === 'Confirmado' ? 't-ok' : 't-warn');

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_580px] gap-3" data-testid="agenda-page">
      {/* calendar */}
      <div className="ent-panel">
        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border">
          <h3 className="font-display font-bold text-[15px] tracking-tight">{MONTHS[cursor.m]} {cursor.y}</h3>
          <div className="flex items-center gap-1.5">
            <button data-testid="cal-prev" onClick={() => move(-1)} className="ent-btn ent-btn-icon"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-next" onClick={() => move(1)} className="ent-btn ent-btn-icon"><ChevronRight className="w-4 h-4" /></button>
            <button data-testid="cal-today" onClick={() => { setCursor({ y: today.getFullYear(), m: today.getMonth() }); setSelDay(todayIso); }} className="ent-btn ent-btn-sm">Hoje</button>
            <span className="ent-btn ent-btn-sm pointer-events-none">Mês</span>
          </div>
        </div>
        <div className="ent-cal">
          {WD.map((w) => <div key={w} className="ent-cal-dow">{w}</div>)}
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="ent-cal-day" />;
            const dstr = iso(cursor.y, cursor.m, d);
            const dayAppts = byDay(dstr).sort((a, b) => (a.hora || '').localeCompare(b.hora || ''));
            const isToday = dstr === todayIso;
            const isSel = dstr === selDay;
            return (
              <button key={i} data-testid={`cal-day-${dstr}`} onClick={() => setSelDay(dstr)} className={`ent-cal-day ${isSel ? 'ent-cal-day-sel' : ''}`}>
                <span className={`num text-[12px] font-semibold ${isToday ? 'text-primary' : 'text-foreground/80'}`}>{d}</span>
                {dayAppts.length > 0 && <span className="text-[10px] t-label">{dayAppts.length} compromisso{dayAppts.length > 1 ? 's' : ''}</span>}
                {dayAppts.slice(0, 3).map((a) => (
                  <span key={a.id} className="flex items-center gap-1.5 num text-[10.5px] text-muted-foreground">
                    <span className={`w-1.5 h-1.5 rounded-full ${a.status === 'Concluído' ? 'bg-muted-foreground/40' : 'bg-primary'}`} />{a.hora}
                  </span>
                ))}
                {dayAppts.length > 3 && <span className="text-[10px] t-label">+{dayAppts.length - 3}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* day panel */}
      <div className="ent-panel flex flex-col">
        <div className="flex items-start justify-between gap-3 px-3.5 py-2.5 border-b border-border">
          <div>
            <h3 className="font-display font-bold text-[15px] tracking-tight" data-testid="agenda-day-title">{longDate}</h3>
            <p className="text-[11px] t-label mt-0.5">{selAppts.length} compromisso{selAppts.length === 1 ? '' : 's'}</p>
          </div>
          <Dialog open={dOpen} onOpenChange={setDOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-appointment-btn" onClick={() => openNew(selDay)} className="ent-btn ent-btn-primary shrink-0"><Plus className="w-3.5 h-3.5" /> Novo agendamento</button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground">
              <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo agendamento</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Título</label><input data-testid="appt-titulo" className={input} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex: Test-drive Corolla" /></div>
                <div className="col-span-2 relative" ref={suggestRef}>
                  <label className="text-xs text-muted-foreground mb-1.5 block">Cliente {form.clienteId && <span className="text-emerald-400 font-semibold">✓ vinculado à base</span>}</label>
                  <input data-testid="appt-cliente" className={input} value={form.cliente} onChange={(e) => { setForm({ ...form, cliente: e.target.value, clienteId: '' }); setShowSuggest(true); }} onFocus={() => setShowSuggest(true)} placeholder="Digite para buscar cliente..." autoComplete="off" />
                  {showSuggest && suggestions.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-border bg-popover shadow-xl overflow-hidden" data-testid="appt-cliente-suggestions">
                      {suggestions.map((c) => (
                        <button key={c.id} type="button" onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">{initials(c.nome)}</span>
                          <div><p className="text-sm font-medium">{c.nome}</p><p className="text-[10px] text-muted-foreground">{c.telefone} · {c.totalAtendimentos} atendimento(s)</p></div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Tipo</label><select className={input} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{tipos.map((t) => <option key={t} className="bg-popover">{t}</option>)}</select></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Vendedor</label><select className={input} value={form.vendedor} onChange={(e) => setForm({ ...form, vendedor: e.target.value })}><option value="" className="bg-popover">Selecione</option>{team.map((t) => <option key={t.user_id} value={t.name} className="bg-popover">{t.name}</option>)}</select></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Data</label><input type="date" className={input} value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Hora</label><input type="time" className={input} value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <button onClick={() => setDOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                <button data-testid="appt-save-btn" onClick={save} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Agendar'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex-1 overflow-x-auto">
          {selAppts.length === 0 ? (
            <p className="ent-empty">Nenhum agendamento neste dia</p>
          ) : (
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Horário</th><th>Tipo</th><th>Cliente / Veículo</th>
                  <th className="hidden 2xl:table-cell">Responsável</th><th>Status</th><th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {[...selAppts].sort((a, b) => (a.hora || '').localeCompare(b.hora || '')).map((ap) => (
                  <tr key={ap.id} data-testid={`appt-card-${ap.id}`}>
                    <td className="num font-semibold">{ap.hora}</td>
                    <td>
                      <span className="ent-status text-muted-foreground">{tipoIcon[ap.tipo] || <MapPin className="w-3 h-3" />} {ap.tipo}</span>
                    </td>
                    <td>
                      {ap.cliente ? (
                        <button data-testid={`appt-client-btn-${ap.id}`} onClick={() => openHistory(ap)} title="Ver histórico do cliente" className="text-left hover:text-primary transition-colors">
                          <p className="font-medium leading-tight">{ap.cliente}</p>
                          <p className="text-[11px] t-label">{ap.titulo}</p>
                        </button>
                      ) : <p className="font-medium">{ap.titulo}</p>}
                    </td>
                    <td className="text-muted-foreground hidden 2xl:table-cell">{ap.vendedor || ap.criadoPor || '—'}</td>
                    <td><span className={`ent-status ${statusCls(ap.status)}`}><span className="ent-dot" />{ap.status || 'Pendente'}</span></td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`appt-toggle-${ap.id}`} title={ap.status === 'Concluído' ? 'Reabrir' : 'Marcar concluído'} onClick={() => toggleStatus(ap)} className={`ent-btn ent-btn-icon border-0 ${ap.status === 'Concluído' ? 't-ok' : ''}`}><Check className="w-3.5 h-3.5" /></button>
                        {ap.cliente && <button data-testid={`appt-history-${ap.id}`} title="Histórico" onClick={() => openHistory(ap)} className="ent-btn ent-btn-icon border-0"><History className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t border-border">
          <div className="px-3.5 pt-2.5 pb-1 flex items-center justify-between">
            <p className="text-[12px] font-semibold">Resumo do dia</p>
            <p className="num text-[11px] t-label">{selDay.split('-').reverse().join('/')}</p>
          </div>
          <div className="ent-ribbon border-0 rounded-none" data-testid="agenda-day-summary">
            <div><p className="ent-label">Agendamentos</p><p className="ent-value mt-1">{resumo.total}</p></div>
            <div><p className="ent-label">Confirmados</p><p className="ent-value mt-1 t-ok">{resumo.confirmados}</p></div>
            <div><p className="ent-label">Pendentes</p><p className="ent-value mt-1 t-warn">{resumo.pendentes}</p></div>
            <div><p className="ent-label">Concluídos</p><p className="ent-value mt-1">{resumo.concluidos}</p></div>
            <div><p className="ent-label">Cancelados</p><p className="ent-value mt-1 t-bad">{resumo.cancelados}</p></div>
          </div>
        </div>
      </div>

      {history && <CustomerHistoryModal data={history} onClose={() => setHistory(null)} />}
    </div>
  );
};

export default Agenda;
